import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useElectronApiReady, useIsOnline } from "src/hooks";
import type { IDatabaseHealth, ISyncErrorEntry } from "src/electron";
import { useDispatch } from "src/redux/hooks";
import { setPendingRequests } from "src/redux/slices/pendingRequestsSlice";
import { Notification } from "src/utils/Notification";

export interface IDesktopDatabaseSyncState {
  isSyncing: boolean;
  progress: number;
  reloadIterator: number;
  isLoadingVisible: boolean;
  syncStep: string;
  syncErrors: ISyncErrorEntry[];
  databaseHealth: IDatabaseHealth | null;
}

const DesktopSyncContext = createContext<IDesktopDatabaseSyncState | null>(
  null,
);

const formatHealthSummary = (health: IDatabaseHealth) => {
  if (health.ready) {
    return `DB v${health.actualVersion ?? "?"} · users ${
      health.tables.find(table => table.name === "users")?.count ?? 0
    } · cache ${
      health.tables.find(table => table.name === "cache")?.count ?? 0
    }`;
  }

  return health.reason ?? "Local database is not ready";
};

const useDesktopDatabaseSyncState = (): IDesktopDatabaseSyncState => {
  const [reloadIterator, setReloadIterator] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [syncStep, setSyncStep] = useState("idle");
  const [syncErrors, setSyncErrors] = useState<ISyncErrorEntry[]>([]);
  const [databaseHealth, setDatabaseHealth] = useState<IDatabaseHealth | null>(
    null,
  );
  const isOnline = useIsOnline();
  const electronApiReady = useElectronApiReady();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const prevOnlineRef = useRef<boolean | null>(null);

  const refreshDiagnostics = async () => {
    if (!window.api?.getSyncDiagnostics) {
      return;
    }

    setSyncErrors(window.api.getSyncDiagnostics().errors);

    if (window.api.getDatabaseHealth) {
      try {
        setDatabaseHealth(await window.api.getDatabaseHealth());
      } catch {
        setDatabaseHealth(null);
      }
    }
  };

  useEffect(() => {
    if (!electronApiReady || !window.api) {
      return;
    }

    window.api.onPendingChange(pending => {
      dispatch(setPendingRequests(pending));
    });

    window.api.updatePending();

    void refreshDiagnostics();

    return () => {
      window.api?.onPendingChange(null);
    };
  }, [dispatch, electronApiReady]);

  useEffect(() => {
    if (!electronApiReady || !window.api) {
      return;
    }

    window.api.onSyncBegin(() => {
      setIsSyncing(true);
      setSyncErrors([]);
      setSyncStep("starting");
    });
    window.api.onSyncStep(step => {
      setSyncStep(step);
    });
    window.api.onSyncChange((started, completed) => {
      setProgress(
        started > 0 ? Number(((completed / started) * 100).toFixed(0)) : 0,
      );
    });
    window.api.onSyncError(message => {
      void refreshDiagnostics();
      Notification("Sync Error", message, "red");
    });
    window.api.onSyncEnd(async () => {
      setIsSyncing(false);
      setProgress(0);
      await refreshDiagnostics();

      const diagnostics = window.api?.getSyncDiagnostics?.();
      let health: IDatabaseHealth | null = null;

      if (window.api?.getDatabaseHealth) {
        try {
          health = await window.api.getDatabaseHealth();
          setDatabaseHealth(health);
        } catch {
          health = null;
        }
      }

      if (diagnostics?.errorCount) {
        Notification(
          "Sync finished with errors",
          `${diagnostics.errorCount} issue(s). Latest: ${diagnostics.errors.at(-1)?.message ?? "Unknown error"}`,
          diagnostics.hasDatabaseErrors ? "red" : "yellow",
        );
      }

      if (health && !health.ready) {
        Notification(
          "Database health check",
          formatHealthSummary(health),
          "red",
        );
      }

      if (lbeidOrPartId?.includes("new=")) {
        navigate(`/logbook/${logbookId}/${idType}`);
      }

      let shouldReload = true;
      if (window.api?.getLocalDataStatus) {
        try {
          const { users, cache } = await window.api.getLocalDataStatus();
          if (users === 0 || cache === 0) {
            Notification(
              "Offline data incomplete",
              "Local user or cache data is missing. Stay online and use the sync button to try again.",
              "yellow",
            );
            shouldReload = false;
          }
        } catch {
          shouldReload = false;
        }
      }

      if (shouldReload) {
        setReloadIterator(prev => prev + 1);
      }
    });

    return () => {
      window.api?.onSyncBegin(null);
      window.api?.onSyncStep(null);
      window.api?.onSyncChange(null);
      window.api?.onSyncEnd(null);
      window.api?.onSyncError(null);
    };
  }, [electronApiReady, idType, lbeidOrPartId, logbookId, navigate]);

  useEffect(() => {
    if (!electronApiReady || !window.api?.sync) {
      return;
    }
    if (!localStorage.getItem("authData")) {
      return;
    }

    const prevOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (!isOnline) {
      return;
    }

    const cameOnline = prevOnline === false || prevOnline === null;
    if (cameOnline) {
      window.api.sync();
    }
  }, [electronApiReady, isOnline]);

  return {
    isSyncing,
    progress,
    reloadIterator,
    isLoadingVisible: isSyncing && isOnline,
    syncStep,
    syncErrors,
    databaseHealth,
  };
};

export const DesktopSyncProvider = ({ children }: { children: ReactNode }) => {
  const value = useDesktopDatabaseSyncState();
  return (
    <DesktopSyncContext.Provider value={value}>
      {children}
    </DesktopSyncContext.Provider>
  );
};

export const useDesktopDatabaseSync = (): IDesktopDatabaseSyncState => {
  const context = useContext(DesktopSyncContext);
  if (!context) {
    return {
      isSyncing: false,
      progress: 0,
      reloadIterator: 0,
      isLoadingVisible: false,
      syncStep: "idle",
      syncErrors: [],
      databaseHealth: null,
    };
  }
  return context;
};
